import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { SourceHealthCard } from "@/components/SourceHealthCard";
import { Loader2, Database, Globe, Users, Wand2, KeyRound, Trash2, CheckCircle2, Clock, AlertTriangle } from "lucide-react";

const KIND_ICON = { registry: Database, serp: Globe, people_data: Users, enrichment: Wand2, tenant_data: Database };

const CredentialRow = ({ provider, status, note, userId, onChange }) => {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/prospecting/credentials/${userId}/${provider}`, { secret: value });
      onChange(r.data.credentials); setValue(""); toast.success(`${status.label} key saved`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save key"); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try {
      const r = await axios.delete(`${API}/prospecting/credentials/${userId}/${provider}`);
      onChange(r.data.credentials); toast.success("Key removed");
    } catch { toast.error("Could not remove key"); } finally { setBusy(false); }
  };
  const tone = status.source === "byok" ? "text-emerald-300" : status.source === "platform" ? "text-sky-300" : "text-gray-500";
  return (
    <div className="py-2 border-b border-white/5" data-testid={`credential-row-${provider}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-56">
          <p className="text-sm text-gray-200">{status.label}</p>
          <p className={`text-[11px] ${tone}`} data-testid={`credential-status-${provider}`}>
            {status.source === "byok" ? `Your key ${status.hint}` : status.source === "platform" ? "Platform key (default)" : "No key — not active"}
          </p>
        </div>
        <Input type="password" value={value} onChange={(e) => setValue(e.target.value)} placeholder={status.source === "byok" ? "Replace key…" : "Paste your API key to override"}
          className="h-8 text-xs bg-black/30 border-white/10 flex-1 min-w-[180px]" data-testid={`credential-input-${provider}`} />
        <Button size="sm" onClick={save} disabled={busy || value.length < 8} className="h-8 bg-ism-teal hover:bg-ism-teal/90 text-white" data-testid={`credential-save-${provider}`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <KeyRound className="h-3 w-3" />}
        </Button>
        {status.source === "byok" && (
          <Button size="sm" variant="outline" onClick={remove} disabled={busy} className="h-8 border-white/10" data-testid={`credential-delete-${provider}`}><Trash2 className="h-3 w-3" /></Button>
        )}
      </div>
      {note && (
        <p className="mt-1.5 flex items-start gap-1.5 text-[11px] text-amber-300/90" data-testid={`credential-note-${provider}`}>
          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />{note}
        </p>
      )}
    </div>
  );
};

export const SourceSettings = ({ userId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    () => axios.get(`${API}/prospecting/sources/${userId}`).then((r) => setData(r.data)).catch(() => toast.error("Could not load data sources")).finally(() => setLoading(false)),
    [userId]
  );
  useEffect(() => { load(); }, [load]);

  const toggle = async (sourceId, enabled) => {
    try { await axios.put(`${API}/prospecting/sources/${userId}/${sourceId}`, { enabled }); load(); }
    catch { toast.error("Could not update source"); }
  };

  // Providers grouped for display, keeping only those the tenant has a credential slot for.
  const credentialGroups = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.providerGroups || { Providers: data.providers })
      .map(([group, providers]) => [group, providers.filter((p) => data.credentials[p])]);
  }, [data]);

  if (loading) return <div className="text-gray-400 text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading data sources…</div>;
  if (!data) return null;
  const byId = Object.fromEntries(data.manifests.map((m) => [m.id, m]));

  return (
    <div className="space-y-5" data-testid="source-settings">
      <SourceHealthCard userId={userId} />
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
        <p className="text-xs text-gray-500 uppercase tracking-wider">Active search provider</p>
        <p className="text-sm text-white" data-testid="active-search-provider">{data.searchProvider.label}</p>
        <p className="text-[11px] text-gray-500">{data.searchProvider.id === "ddg" ? "Add a Serper, Brave or Exa key below to upgrade from the free fallback. " : "Google-quality results via the platform key. "}Your keys override platform keys.</p>
      </div>

      <div>
        <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Source plan for your targeting spec</p>
        <div className="space-y-2">
          {data.plan.map((step) => {
            const m = byId[step.sourceId] || {};
            const Icon = KIND_ICON[m.kind] || Database;
            const userDisabled = data.config?.[step.sourceId]?.enabled === false;
            return (
              <div key={step.sourceId} className={`rounded-xl border p-3 ${step.enabled ? "border-ism-teal/30 bg-ism-teal/5" : "border-white/10 bg-white/[0.02]"}`} data-testid={`source-card-${step.sourceId}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2">
                    <Icon className={`h-4 w-4 mt-0.5 ${step.enabled ? "text-ism-teal" : "text-gray-500"}`} />
                    <div>
                      <p className="text-sm text-white">{m.name} <span className="text-[10px] text-gray-500 uppercase ml-1">{m.kind} · {step.role}</span></p>
                      <p className="text-xs text-gray-400">{m.description}</p>
                      <p className="text-[11px] text-gray-500 mt-1">Fit {Math.round(step.score * 100)}% · {step.reasons.join(" · ")}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {m.status === "planned" ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-amber-300"><Clock className="h-3 w-3" />Planned</span>
                    ) : step.credentialStatus === "missing" ? (
                      <span className="text-[11px] text-gray-500">Needs key</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300"><CheckCircle2 className="h-3 w-3" />{step.enabled ? "Active" : "Off"}</span>
                    )}
                    {m.status === "available" && step.credentialStatus !== "missing" && (
                      <button onClick={() => toggle(step.sourceId, userDisabled)} className="text-[11px] text-gray-400 hover:text-white underline" data-testid={`source-toggle-${step.sourceId}`}>
                        {userDisabled ? "Enable" : "Disable"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Provider keys (bring your own)</p>
        <p className="text-[11px] text-gray-500 mb-2">Public registries (SEC, NPPES, CMS, IRS 990, GLEIF) need no key. Apollo.io / People Data Labs find decision-makers at verified firms; Proxycurl and Hunter.io prove LinkedIn profiles and work emails before outreach. Without a people-data key, buyers are AI-inferred and clearly labeled.</p>
        {credentialGroups.map(([group, providers]) => (
          <div key={group} className="rounded-xl border border-white/10 bg-white/[0.03] px-3 mb-2" data-testid={`credential-group-${group.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <p className="text-[11px] text-gray-400 pt-2">{group}</p>
            {providers.map((p) => (
              <CredentialRow key={p} provider={p} status={data.credentials[p]} note={data.providerNotes?.[p]} userId={userId} onChange={(creds) => { setData({ ...data, credentials: creds }); load(); }} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
